import React, { useState, useEffect, useCallback } from 'react';
import * as dpService from 'features/dp/services/dpService';

/**
 * ValesTab – "Vales" sub-tab (salary advances / adiantamentos).
 * CRUD for adiantamentos with competência selector and status badges.
 */

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getNome = (item) =>
  item.funcionario?.pessoa?.nome ?? '-';

const statusBadge = (st) => {
  let cls =
    st === 'pendente'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-green-100 text-green-700';
  if (st === 'cancelado') cls = 'bg-gray-100 text-gray-500';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {st.charAt(0).toUpperCase() + st.slice(1)}
    </span>
  );
};

export default function ValesTab() {
  const now = new Date();
  const defaultComp = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');

  const [comp, setComp] = useState(defaultComp);
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [funcs, setFuncs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form fields
  const [fid, setFid] = useState('');
  const [desc, setDesc] = useState('Adiantamento salarial');
  const [val, setVal] = useState('');
  const [dt, setDt] = useState(now.toISOString().split('T')[0]);
  const [obs, setObs] = useState('');

  // ── Load adiantamentos for selected competência ──
  const carregar = useCallback(() => {
    setLoading(true);
    dpService
      .listarAdiantamentos({ competencia: comp })
      .then((r) => setDados(r.data?.data || []))
      .catch(() => setDados([]))
      .finally(() => setLoading(false));
  }, [comp]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Load active employees ──
  useEffect(() => {
    dpService
      .listarFuncionarios({ status: 'ativo', per_page: 100 })
      .then((r) => {
        const d = r.data?.data;
        setFuncs(d?.data ?? d ?? []);
      })
      .catch(() => {});
  }, []);

  // ── Form helpers ──
  const resetForm = () => {
    setFid('');
    setDesc('Adiantamento salarial');
    setVal('');
    setDt(now.toISOString().split('T')[0]);
    setObs('');
    setEditId(null);
  };

  const salvar = () => {
    setSaving(true);
    const body = {
      funcionario_id: Number(fid),
      competencia: comp,
      descricao: desc,
      valor: parseFloat(val) || 0,
      dt_adiantamento: dt,
      observacoes: obs,
    };

    const promise = editId
      ? dpService.atualizarAdiantamento(editId, body)
      : dpService.criarAdiantamento(body);

    promise
      .then(() => {
        resetForm();
        setShowForm(false);
        carregar();
      })
      .catch((e) => {
        window.alert(e.response?.data?.message || 'Erro ao salvar');
      })
      .finally(() => setSaving(false));
  };

  const excluir = (id) => {
    if (!window.window.confirm('Excluir este adiantamento?')) return;
    dpService
      .excluirAdiantamento(id)
      .then(() => carregar())
      .catch(() => window.alert('Erro ao excluir'));
  };

  const editar = (item) => {
    setEditId(item.id);
    setFid(String(item.funcionario_id));
    setDesc(item.descricao);
    setVal(String(item.valor));
    setDt(item.dt_adiantamento ? item.dt_adiantamento.split('T')[0] : '');
    setObs(item.observacoes || '');
    setShowForm(true);
  };

  return (
    <div className="space-y-4">
      {/* ── Competência selector + New button ── */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-[color:var(--text-muted)]">
          Competência:
        </label>
        <input
          type="month"
          value={comp}
          onChange={(e) => setComp(e.target.value)}
          className="border border-[color:var(--border)] rounded-lg px-3 py-1.5 text-sm bg-[color:var(--surface)] text-[color:var(--text)]"
        />
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="ml-auto px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700"
        >
          {showForm ? 'Cancelar' : '+ Novo Vale'}
        </button>
      </div>

      {/* ── Form ── */}
      {showForm && (
        <div className="border border-[color:var(--border)] rounded-2xl p-4 space-y-3 bg-[color:var(--surface-muted)]">
          <h4 className="font-semibold text-sm text-[color:var(--text)]">
            {editId ? 'Editar Adiantamento' : 'Novo Adiantamento'}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Funcionário */}
            <div>
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
                Funcionário
              </label>
              <select
                value={fid}
                onChange={(e) => setFid(e.target.value)}
                disabled={!!editId}
                className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
              >
                <option value="">Selecione...</option>
                {funcs.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.pessoa ? f.pessoa.nome : f.nome || f.id}
                  </option>
                ))}
              </select>
            </div>

            {/* Valor */}
            <div>
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
                Valor (R$)
              </label>
              <input
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                step="0.01"
                placeholder="0,00"
                className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
                Descrição
              </label>
              <input
                type="text"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
              />
            </div>

            {/* Data */}
            <div>
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
                Data
              </label>
              <input
                type="date"
                value={dt}
                onChange={(e) => setDt(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { resetForm(); setShowForm(false); }}
              className="px-3 py-1.5 text-sm rounded-xl border border-[color:var(--border)] hover:bg-[color:var(--surface)]"
            >
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={saving || !fid || !val}
              className="px-4 py-1.5 text-sm font-medium rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {/* ── Table / empty / loading ── */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full" />
        </div>
      ) : dados.length === 0 ? (
        <div className="text-center py-12 text-[color:var(--text-muted)] text-sm">
          Nenhum adiantamento registrado nesta competência.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-xs min-w-[700px]">
            <thead>
              <tr className="bg-[color:var(--surface-muted)] border-b border-[color:var(--border)]">
                <th className="p-2 text-left font-semibold text-[color:var(--text-muted)]">Funcionário</th>
                <th className="p-2 text-left font-semibold text-[color:var(--text-muted)]">Descrição</th>
                <th className="p-2 text-right font-semibold text-[color:var(--text-muted)]">Valor</th>
                <th className="p-2 text-center font-semibold text-[color:var(--text-muted)]">Data</th>
                <th className="p-2 text-center font-semibold text-[color:var(--text-muted)]">Status</th>
                <th className="p-2 text-center font-semibold text-[color:var(--text-muted)]">Ações</th>
              </tr>
            </thead>
            <tbody>
              {dados.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface-muted)]"
                >
                  <td className="p-2 font-medium text-[color:var(--text)]">{getNome(item)}</td>
                  <td className="p-2 text-[color:var(--text-muted)]">{item.descricao}</td>
                  <td className="p-2 text-right font-medium text-[color:var(--text)]">{fmtBRL(item.valor)}</td>
                  <td className="p-2 text-center text-[color:var(--text-muted)]">
                    {item.dt_adiantamento
                      ? new Date(item.dt_adiantamento).toLocaleDateString('pt-BR')
                      : '—'}
                  </td>
                  <td className="p-2 text-center">{statusBadge(item.status)}</td>
                  <td className="p-2 text-center">
                    {item.status === 'pendente' ? (
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={() => editar(item)}
                          className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-600 hover:bg-blue-100"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => excluir(item.id)}
                          className="px-2 py-1 text-xs rounded bg-red-50 text-red-600 hover:bg-red-100"
                        >
                          Excluir
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[color:var(--text-muted)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
